import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, X, ArrowUpRight, MessageCircle } from "lucide-react";

const WA = "https://wa.me/393519261622";

// Risposte predefinite (offline, nessun consumo AI)
const TOPICS = [
  {
    q: "Quali servizi offrite?",
    a: "Ci occupiamo di ristrutturazioni chiavi in mano, impermeabilizzazione in poliurea, coibentazione in poliuretano, efficientamento energetico, pareti ventilate, rivestimenti per piscine, rivestimenti protettivi anticorrosivi, restauro conservativo e finiture con risanamento dell'umidità di risalita.",
    to: "/servizi",
    linkLabel: "Vedi tutti i servizi",
  },
  {
    q: "Cos'è l'impermeabilizzazione in poliurea?",
    a: "È una membrana continua applicata a spruzzo, senza giunti, impermeabile in pochi secondi e resistente ad agenti chimici, UV e sbalzi termici. Ideale per coperture, terrazzi, vasche e piscine.",
    to: "/impermeabilizzazione-poliurea",
    linkLabel: "Scopri la poliurea",
  },
  {
    q: "Trattate l'umidità di risalita?",
    a: "Sì. Interveniamo con un sistema di risanamento traspirante a basso spessore, senza demolizioni invasive, che riporta la muratura in equilibrio. È parte delle nostre finiture e ristrutturazioni.",
    to: "/finiture-e-rivestimenti",
    linkLabel: "Finiture e umidità",
  },
  {
    q: "Fate ristrutturazioni complete?",
    a: "Sì, gestiamo l'intero cantiere chiavi in mano: un unico interlocutore, tempi certi e materiali certificati, dalla demolizione alle finiture.",
    to: "/ristrutturazioni-chiavi-in-mano",
    linkLabel: "Ristrutturazioni chiavi in mano",
  },
  {
    q: "In quali zone operate?",
    a: "Abbiamo sede a Monopoli (BA) e operiamo in tutta la Puglia e sull'intero territorio nazionale.",
    to: "/contatti",
    linkLabel: "Contatti",
  },
  {
    q: "Il preventivo è gratuito?",
    a: "Sì. Il sopralluogo e il preventivo sono gratuiti e senza impegno. Ti ricontattiamo generalmente entro 24-48 ore.",
    to: "/preventivo",
    linkLabel: "Richiedi un preventivo",
  },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Ciao e benvenuto in Greenhouse Solutions SPA! Sono l'assistente virtuale. Come posso aiutarti? Scegli una domanda qui sotto." },
  ]);
  const [asked, setAsked] = useState([]);
  const scrollRef = useRef(null);

  const questionsCount = asked.length;
  const showWhatsapp = questionsCount >= 2;
  const remaining = TOPICS.filter((t) => !asked.includes(t.q));

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showWhatsapp]);

  const handleTopic = (topic) => {
    setMessages((prev) => [
      ...prev,
      { role: "user", text: topic.q },
      { role: "bot", text: topic.a, to: topic.to, linkLabel: topic.linkLabel },
    ]);
    setAsked((prev) => (prev.includes(topic.q) ? prev : [...prev, topic.q]));
  };

  return (
    <div className="fixed bottom-5 right-5 z-[120] flex flex-col items-end gap-3" data-testid="chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="w-[min(360px,calc(100vw-2.5rem))] bg-card border border-border shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-primary px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center bg-primary-foreground/15">
                <MessagesSquare className="w-5 h-5 text-primary-foreground" />
              </span>
              <div className="min-w-0">
                <p className="font-head font-bold text-primary-foreground leading-none">Greenhouse Solutions</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/80 mt-1">Assistente virtuale</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                aria-label="Chiudi chat"
                className="ml-auto text-primary-foreground/80 hover:text-primary-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="p-4 space-y-3 overflow-y-auto max-h-[46vh] min-h-[220px] bg-secondary/30">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-foreground text-background"
                        : "bg-card border border-border text-foreground"
                    }`}
                  >
                    {m.text}
                    {m.to && (
                      <Link
                        to={m.to}
                        onClick={() => setOpen(false)}
                        className="mt-2 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-primary hover:gap-2 transition-all"
                      >
                        {m.linkLabel || "Scopri di più"} <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}

              {showWhatsapp && (
                <motion.a
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chat-whatsapp-cta"
                  className="flex items-center gap-3 bg-[#25D366] text-white px-4 py-3 hover:bg-[#128C7E] transition-colors"
                >
                  <MessageCircle className="w-5 h-5 shrink-0" />
                  <span className="text-sm font-medium leading-tight">Vuoi maggiori informazioni? Scrivici su WhatsApp, ti rispondiamo subito.</span>
                </motion.a>
              )}
            </div>

            {/* Quick replies */}
            <div className="p-3 border-t border-border bg-card space-y-2">
              <Link
                to="/preventivo"
                onClick={() => setOpen(false)}
                data-testid="chat-preventivo-shortcut"
                className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-foreground hover:text-background transition-colors"
              >
                Richiedi un preventivo <ArrowUpRight className="w-4 h-4" />
              </Link>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 px-1 pt-1">
                {remaining.length ? "Domande frequenti" : "Hai altre domande?"}
              </p>
              {remaining.length > 0 ? (
                <div className="flex flex-col gap-2 max-h-[26vh] overflow-y-auto">
                  {remaining.map((t) => (
                    <button
                      key={t.q}
                      onClick={() => handleTopic(t)}
                      data-testid={`chat-topic-${TOPICS.indexOf(t)}`}
                      className="text-left text-sm px-3 py-2.5 border border-border hover:border-primary hover:text-primary transition-colors"
                    >
                      {t.q}
                    </button>
                  ))}
                </div>
              ) : (
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chat-whatsapp-final"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 text-sm font-medium hover:bg-[#128C7E] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Continua su WhatsApp
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => { setOpen((v) => !v); setSeen(true); }}
        data-testid="chat-toggle"
        aria-label="Apri la chat"
        className="relative w-14 h-14 grid place-items-center bg-primary text-primary-foreground shadow-lg hover:scale-105 active:scale-95 transition-transform"
      >
        {!open && <span className="absolute inline-flex h-full w-full animate-ping bg-primary opacity-25" />}
        {!open && !seen && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            data-testid="chat-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 grid place-items-center bg-[#25D366] text-white text-[11px] font-bold rounded-full border-2 border-background z-10"
          >
            1
          </motion.span>
        )}
        {open ? <X className="w-7 h-7 relative" /> : <MessagesSquare className="w-7 h-7 relative" />}
      </button>
    </div>
  );
}
